import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc
} from 'firebase/firestore';
import { db } from './firebase';

const NOTIFICATIONS_COLLECTION = 'notifications';

/**
 * Subscribe to all notifications with real-time updates
 */
export const subscribeToNotifications = (callback) => {
  const q = query(
    collection(db, NOTIFICATIONS_COLLECTION),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(notifications);
  });
};

/**
 * Add a new notification
 */
export const addNotification = async (notificationData) => {
  try {
    const docRef = await addDoc(collection(db, NOTIFICATIONS_COLLECTION), {
      ...notificationData,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp(),
      sentAt: serverTimestamp(),
      status: 'sent',
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding notification:', error);
    throw error;
  }
};

/**
 * Update a notification
 */
export const updateNotification = async (notificationId, updates) => {
  try {
    const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await updateDoc(notificationRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating notification:', error);
    throw error;
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await deleteDoc(notificationRef);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Schedule notification (sets scheduledFor field)
 */
export const scheduleNotification = async (notificationData, scheduledFor) => {
  try {
    const docRef = await addDoc(collection(db, NOTIFICATIONS_COLLECTION), {
      ...notificationData,
      scheduledFor: scheduledFor,
      status: 'scheduled',
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error scheduling notification:', error);
    throw error;
  }
};

/**
 * Calculate notification statistics
 */
export const calculateNotificationStats = (notifications) => {
  const total = notifications.length;
  const sent = notifications.filter(n => n.status === 'sent').length;
  const scheduled = notifications.filter(n => n.status === 'scheduled').length;
  const failed = notifications.filter(n => n.status === 'failed').length;

  // Calculate today's sent
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todaySent = notifications.filter(n => {
    if (n.status !== 'sent') return false;
    const sentDate = n.sentAt?.toDate?.() || new Date(n.sentAt);
    return sentDate >= today;
  }).length;

  // Count by audience type
  const audienceCounts = {};
  notifications.forEach(n => {
    const audience = n.audience || 'unknown';
    audienceCounts[audience] = (audienceCounts[audience] || 0) + 1;
  });

  return {
    total,
    sent,
    scheduled,
    failed,
    todaySent,
    audienceCounts,
  };
};

export default {
  subscribeToNotifications,
  addNotification,
  updateNotification,
  deleteNotification,
  scheduleNotification,
  calculateNotificationStats,
};
